import { useState } from 'react'
import type { FormEvent } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useFetchUncachedCb } from '@gluedigital/ruse-fetch-extras'
import { useDialog } from '@hooks/useDialog'
import Toast from '@components/Toast/Toast'
import { ADMIN_URL } from 'src/constants/global'
import type { User } from 'src/types/global'

import './DeleteUserDialog.css'

interface DeleteUserDialogProps {
  user: User
  onClose: () => void
  onDeleted: () => void
}

export default function DeleteUserDialog({
  user,
  onClose,
  onDeleted
}: DeleteUserDialogProps) {
  const intl = useIntl()
  const { dialogRef } = useDialog(true, onClose)
  const doDelete = useFetchUncachedCb()

  const [reason, setReason] = useState('')
  const [error, setError] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleConfirm = async (e: FormEvent) => {
    e.preventDefault()
    if (!reason.trim()) return

    setIsSubmitting(true)
    setError('')
    try {
      await doDelete(`${ADMIN_URL}/users/${user.id}`, {
        method: 'DELETE',
        body: { reason: reason.trim() }
      })
      onDeleted()
      onClose()
    } catch (err) {
      const payload = (err as { payload?: { error?: string } })?.payload
      setError(
        payload?.error ||
          intl.formatMessage({ id: 'user-delete.error.generic' })
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <dialog
      id="delete-user-dialog"
      className="app-dialog"
      ref={dialogRef}
      onCancel={onClose}
    >
      <form className="dialog-inner" onSubmit={handleConfirm}>
        <h3>
          <FormattedMessage id="user-delete.title" />
        </h3>
        <p>
          <FormattedMessage
            id="user-delete.confirm-message"
            values={{ name: user.name }}
          />
        </p>
        <label htmlFor="delete-user-reason">
          <strong>
            <FormattedMessage id="user-delete.reason" />
          </strong>
          <textarea
            id="delete-user-reason"
            rows={3}
            required
            value={reason}
            disabled={isSubmitting}
            placeholder={intl.formatMessage({
              id: 'user-delete.reason-placeholder'
            })}
            onChange={(e) => setReason(e.target.value)}
          />
        </label>
        <div className="dialog-actions">
          <button type="button" onClick={onClose} disabled={isSubmitting}>
            <FormattedMessage id="cancel" />
          </button>
          <button type="submit" disabled={isSubmitting}>
            <FormattedMessage id="delete" />
          </button>
        </div>
      </form>
      {error && <Toast message={error} onClose={() => setError('')} />}
    </dialog>
  )
}
